/**
 * Hub Status — YamanekoGames Hub の到達可否を表すグローバルな観測ストア。
 *
 * `navigator.onLine` は OS レベルのネットワーク状態しか反映しないため、
 * Hub 側障害（5xx・bootstrap 失敗・CORS 等）を観測するには別管理が必要。
 *
 * States:
 *   'unknown' — 初回プローブ完了前
 *   'online'  — 直近の Hub 通信が成功
 *   'offline' — 直近の通信が失敗（ネットワーク / 5xx / 初期化失敗）
 *
 * 設計方針（Sweeper `hubStatus.ts` 準拠）:
 *   - リーフモジュール（依存なし）。SDK 初期化パスからも安全に呼べる。
 *   - 同値書き込みは無視。サブスクライバには変化時のみ通知。
 *   - React 統合は useSyncExternalStore で tear-free 読み取り。
 */

import { useSyncExternalStore } from 'react'

export type HubStatus = 'unknown' | 'online' | 'offline'

type Listener = (status: HubStatus) => void

let currentStatus: HubStatus = 'unknown'
let lastChangedAt = 0
const listeners = new Set<Listener>()

export function getHubStatus(): HubStatus {
  return currentStatus
}

export function getHubStatusLastChangedAt(): number {
  return lastChangedAt
}

/**
 * Hub status を更新する。同値なら no-op。変化時のみサブスクライバへ通知。
 */
export function setHubStatus(next: HubStatus): void {
  if (next === currentStatus) return
  const previous = currentStatus
  currentStatus = next
  lastChangedAt = Date.now()
  console.log(`[hubStatus] ${previous} -> ${next}`)
  for (const listener of listeners) {
    try {
      listener(next)
    } catch (err) {
      // バグった subscriber に他を巻き込ませない
      console.warn('[hubStatus] subscriber threw', err)
    }
  }
}

export function subscribeHubStatus(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

// ─── React integration ────────────────────────────────────────────────────

function subscribeForReact(callback: () => void): () => void {
  return subscribeHubStatus(callback)
}

function getSnapshotForReact(): HubStatus {
  return currentStatus
}

/** React hook: 現在の Hub status を返し、変化時に再描画する。 */
export function useHubStatus(): HubStatus {
  return useSyncExternalStore(subscribeForReact, getSnapshotForReact, getSnapshotForReact)
}
